import type { ReactElement } from 'react';

import InternalLinks from '@skyscanner-internal/global-components/internal-links';

import AccommodationAmenities from './AccommodationAmenities';
import Breadcrumb from './Breadcrumb';
import ConfidenceMessaging from './ConfidenceMessaging';
import CrossLinks from './CrossLinks';
import DetailedLocation from './DetailedLocation';
import ExploreOtherOptions from './ExploreOtherOptions';
import FastFacts from './FastFacts';
import GoodToKnow from './GoodToKnow';
import HotelDescription from './HotelDescription';
import HotelInformation from './HotelInformation';
import HotelMedia from './HotelMedia';
import HotelOffers from './HotelOffers';
import HotelReviews from './HotelReviews';
import Navigation, {
  NAVIGATION_ITEMS,
  HOTEL_MEDIA,
  HOTEL_PRICES,
  LOCATION,
  REVIEWS,
  GOOD_TO_KNOW,
  AMENITIES,
  SEO_NAVIGATION_ITEMS,
} from './Navigation';

import type { ConfidenceMessagingProps } from './ConfidenceMessaging';
import type { OnStayChange } from './types';
import type { Configs } from '@shared/types/configs';
import type { DetailsPageProps } from '@shared/types/details-page-props';
import type { ReviewsStore } from '@shared/types/frontend/reviews-store';
import type {
  HotelContent,
  HotelAmenityCategories,
  HotelDescription as HotelDescriptionType,
} from '@shared/types/hotel-content';
import type { I18nService } from '@shared/types/i18n';
import type { FormatDate } from '@shared/types/i18n/localisation-service';
import type { UserContext } from '@shared/types/user-context';
import type { Nullable, Stay } from '@shared/types/utils';
import type { Destination } from '@shared/ui/types';
import type { NavigationItems } from '@shared/utils-frontend/navigation-service';

import Breadcrumbs from '@hotels-hotel-detail/ui/Breadcrumbs';
import FAQs from '@hotels-hotel-detail/ui/FAQs';
import Section from '@hotels-hotel-detail/ui/Section';
import SectionHeader from '@hotels-hotel-detail/ui/SectionHeader';
import RewardPriceBanner from '@shared/ui/components/RewardPriceBanner';
import ScrollTopButton from '@shared/ui/components/ScrollTopButton';
import { FormattedMessage } from '@shared/ui/components/i18n';
import { getDayviewUrl } from '@shared/utils-frontend/url/url';
import { buildHotelLocation } from '@shared/utils-universal/hotel-location';

export type Props = DetailsPageProps & {
  onStayChange: OnStayChange;
  userContext: UserContext;
  isFromHpa: boolean;
  reviewsStore: ReviewsStore;
  configs: Configs;
  i18n: I18nService;
};

export type HotelOffersProps = DetailsPageProps & {
  onStayChange: OnStayChange;
  userContext: UserContext;
  isFromHpa: boolean;
  configs: Configs;
  i18n: I18nService;
};

export const generateSuggestedSearch = ({
  formatDate,
  searchEntityId,
  stay,
  uBFFHotel: { hotelId, location },
}: {
  uBFFHotel: HotelContent;
  stay: Stay;
  searchEntityId: string | null;
  formatDate: FormatDate;
}) => {
  const entityId =
    searchEntityId ?? location?.geopoliticalParents?.cityId ?? hotelId;
  return getDayviewUrl({ destination: { entityId }, stay }, formatDate);
};

const buildHotelMedia = ({ uBFFHotel }: Props) => (
  <HotelMedia hotelId={uBFFHotel.hotelId} />
);

export const buildHotelOffers = ({
  confidenceProviders,
  onStayChange,
  uBFFHotel,
}: HotelOffersProps) => {
  const destination = { entityId: uBFFHotel.hotelId, entity: uBFFHotel.name };
  return (
    <HotelOffers
      confidenceProviders={confidenceProviders}
      onStayChange={onStayChange}
      destination={destination}
    />
  );
};

export const buildReviews = ({ uBFFHotel: { hotelId } }: Props) => (
  <HotelReviews hotelId={hotelId} />
);

export const buildAccommodationAmenities = ({
  configs,
  uBFFHotel,
  userContext,
}: Props) => {
  const { amenities } = uBFFHotel;
  if (!amenities || amenities.length === 0) {
    return null;
  }

  return (
    <AccommodationAmenities
      amenityCategories={amenities as HotelAmenityCategories}
      collapsed={
        userContext.enableSEOVariantHotelsDetailsPage &&
        configs.enableAmenitiesSEOVariant
      }
    />
  );
};

export const buildBreadcrumbsSectionBottom = ({ breadcrumbsData }: Props) => {
  if (!breadcrumbsData) {
    return null;
  }

  return <Breadcrumbs {...breadcrumbsData} />;
};

export const buildInternalLinks = ({ internalLinks, userContext }: Props) =>
  internalLinks ? (
    <InternalLinks
      {...internalLinks}
      ssr={{
        isMobile: userContext.device.isMobilePhone,
        isTablet: userContext.device.isTablet,
      }}
    />
  ) : null;

const buildCustomizeBreadcrumbsSection = ({ stay, uBFFHotel }: Props) => {
  const { city, cityId, country, countryId } = buildHotelLocation(
    uBFFHotel.location,
  );

  const entity = { id: uBFFHotel.hotelId, name: uBFFHotel.name };

  return (
    <Section
      padded
      node={
        <Breadcrumb
          stay={stay}
          nation={{ id: countryId, name: country }}
          city={{ id: cityId, name: city }}
          entity={entity}
        />
      }
    />
  );
};

const buildExploreOtherOptions = ({ stay, uBFFHotel }: Props) => {
  const { city, cityId } = buildHotelLocation(uBFFHotel.location);
  if (!cityId) {
    return null;
  }
  const destination = {
    entityId: cityId,
    entity: city,
  };

  return <ExploreOtherOptions stay={stay} destination={destination} />;
};

const buildHotelInformation = () => <HotelInformation />;

const buildLocation = ({ uBFFHotel }: Props) => (
  <DetailedLocation uBFFHotel={uBFFHotel} />
);

const buildHotelDescription = ({ uBFFHotel }: Props) => {
  const { description, name } = uBFFHotel;
  if (!description || !description.content) {
    return null;
  }

  return (
    <HotelDescription
      hotelDescription={description as HotelDescriptionType}
      hotelName={name}
    />
  );
};

const hotelInformationTypeToDestination = (
  uBFFHotel: HotelContent,
): Destination => {
  const { hotelId, name } = uBFFHotel;

  return {
    entityId: hotelId,
    entity: name,
    type: 'Hotel',
  };
};

const buildNavigation = (
  { i18n, searchEntityId, stay, uBFFHotel }: Props,
  items: NavigationItems,
) => (
  <Navigation
    backHref={generateSuggestedSearch({
      uBFFHotel,
      stay,
      searchEntityId,
      formatDate: i18n.formatDate,
    })}
    stay={stay}
    items={items}
    destination={hotelInformationTypeToDestination(uBFFHotel)}
  />
);

const buildGoodToKnow = ({ uBFFHotel }: Props) => {
  const { checkinTime, checkoutTime, hotelPolicies } = uBFFHotel;
  if (
    (!hotelPolicies || hotelPolicies.length === 0) &&
    !checkinTime &&
    !checkoutTime
  ) {
    return null;
  }
  return <GoodToKnow />;
};

export const buildFastFacts = ({ configs, uBFFHotel, userContext }: Props) => {
  const { checkinTime, checkoutTime, hotelPolicies } = uBFFHotel;

  if (
    !(
      userContext.enableSEOVariantHotelsDetailsPage &&
      configs.enableFastFactsSEOVariant
    )
  ) {
    return null;
  }

  if (
    (!hotelPolicies || hotelPolicies.length === 0) &&
    !checkinTime &&
    !checkoutTime
  ) {
    return null;
  }

  return (
    <FastFacts
      hotelPolicies={hotelPolicies!}
      checkinTime={checkinTime}
      checkoutTime={checkoutTime}
    />
  );
};

const buildScrollTopButton = () => <ScrollTopButton showMinHeight={1600} />;

export const buildConfidenceMessaging = ({
  configs,
  uBFFHotel,
  userContext,
}: Props) => {
  if (
    !(
      userContext.enableSEOVariantHotelsDetailsPage &&
      configs.enableConfidenceMessageSEOVariant
    )
  ) {
    return null;
  }

  const { city } = buildHotelLocation(uBFFHotel.location);

  const dataList: ConfidenceMessagingProps['dataList'] = [];
  // Hotel city is nullable, so we must check before assuming it exists and
  // adding the confidence message which depends on it.
  if (city) {
    dataList.push({
      descriptionKey: 'ConfidenceMessaging_HDP_find_the_best',
      placeholders: {
        city,
      },
      iconName: 'search',
    });
  }
  dataList.push({
    descriptionKey: 'ConfidenceMessaging_HDP_compare_deals',
    placeholders: { hotel_name: uBFFHotel.name },
    iconName: 'price-tag',
  });
  dataList.push({
    descriptionKey: 'ConfidenceMessaging_HDP_lookout_for_deals',
    placeholders: {},
    iconName: 'hotel-flexible',
  });
  return <ConfidenceMessaging dataList={dataList} />;
};

export const buildFaqs = ({ faqs, uBFFHotel }: Props) => {
  if (!faqs) {
    return null;
  }

  const { name } = uBFFHotel;
  const title = (
    <SectionHeader
      title={
        <FormattedMessage
          label="component_faqs_HDP_faq_title"
          data={{ hotel_name: name }}
        />
      }
    />
  );
  return <FAQs items={faqs.questionAnswers} title={title} />;
};

export const buildCrossLinks = ({ crossLinking }: Props) => {
  if (!crossLinking || crossLinking.links.length === 0) {
    return null;
  }

  return <CrossLinks items={crossLinking.links} />;
};

export const buildRewardPriceBanner = () => (
  <RewardPriceBanner messageType="detail" />
);

const buildCoreComponents = (
  props: Props,
): { [key: string]: Nullable<ReactElement> } => ({
  [HOTEL_PRICES]: buildHotelOffers(props),
  [REVIEWS]: buildReviews(props),
  hotelInformation: buildHotelInformation(),
  [LOCATION]: buildLocation(props),
  breadcrumbsSectionTop: buildCustomizeBreadcrumbsSection(props),
  breadcrumbsSectionBottom: buildBreadcrumbsSectionBottom(props),
  [HOTEL_MEDIA]: buildHotelMedia(props),
  hotelDescription: buildHotelDescription(props),
  [AMENITIES]: buildAccommodationAmenities(props),
  [GOOD_TO_KNOW]: buildGoodToKnow(props),
  scrollTopButton: buildScrollTopButton(),
  exploreOtherOptions: buildExploreOtherOptions(props),
  rewardPriceBanner: buildRewardPriceBanner(),
});

const buildPageComponentsSEO = (props: Props) => ({
  ...buildCoreComponents(props),
  fastFacts: buildFastFacts(props),
  confidenceMessaging: buildConfidenceMessaging(props),
  faqs: buildFaqs(props),
  crossLinks: buildCrossLinks(props),
  internalLinks: buildInternalLinks(props),
  navigation: buildNavigation(
    props,
    SEO_NAVIGATION_ITEMS.filter(({ id }) => buildCoreComponents(props)[id]),
  ),
});

const buildPageComponentsFunnel = (props: Props) => ({
  ...buildCoreComponents(props),
  navigation: buildNavigation(
    props,
    NAVIGATION_ITEMS.filter(({ id }) => buildCoreComponents(props)[id]),
  ),
});

const buildPageComponents = (
  props: Props,
  enableSEOVariantHotelsDetailsPage: boolean,
) => {
  if (enableSEOVariantHotelsDetailsPage) {
    return buildPageComponentsSEO(props);
  }
  return buildPageComponentsFunnel(props);
};

export default buildPageComponents;
